/**
 * ef-048-0c — one shared in-flight-promise de-dupe, extracted out of
 * `useSelectedEntity.ts`'s own `fetchEntitiesDeduped` and `usePendingTime.ts`'s own
 * `fetchPendingDeduped`, which hand-rolled the identical module-scope pattern for the
 * identical reason: React 18 StrictMode's mount→cleanup→mount (`main.tsx` wraps the
 * whole app in `<StrictMode>` unconditionally, dev and prod builds both) fires every
 * effect twice, and — on screens that mount more than one instance of the same hook in
 * the same render wave, e.g. `/farm/fields`'s `EntitySwitcher` plus `Fields.tsx`'s own
 * `useSelectedEntity()` — doubles AGAIN per instance. Sharing the in-flight PROMISE
 * (never the resolved value) collapses all of that back to exactly one real network
 * round trip: every caller racing to mount within the same wave awaits the same
 * request.
 *
 * Deliberately NOT a cache. The shared promise clears itself in `.finally()` the moment
 * it settles, so the next independent call — a later client-side navigation, a poll
 * tick, an explicit `reload()` — always asks the server again rather than risking a
 * stale result.
 */
export function createDedupedFetcher<T>(fetch: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null;

  return function fetchDeduped(): Promise<T> {
    if (!inFlight) {
      inFlight = fetch().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };
}
